<template>
  <div class="loans-pipeline__container" :class="{ 'cx-skin': skinOn }">
    <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-table-cells-large"></i>
        </div>
        <div>
          <h1 class="page-title">Pipeline</h1>
          <p class="page-subtitle">Every loan, every stage, one queue</p>
        </div>
      </div>
    </PageHeaderPortal>

    <!-- Retail is the original pipeline; Reverse / Commercial are the referral apps (components/referrals).
         Badges count referrals with something new since the user last opened them. -->
    <div class="pipeline-tabs" role="tablist">
      <button v-for="tab in tabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="pipeline-tab"
              :class="{ 'is-on': activeTab === tab.key }"
              :aria-selected="activeTab === tab.key"
              @click="selectTab(tab.key)">
        <i :class="tab.icon"></i>
        {{ tab.label }}
        <span v-if="tab.badge" class="pipeline-tab__badge" :title="`${tab.badge} with new activity`">{{ tab.badge }}</span>
      </button>
    </div>

    <div v-show="activeTab === 'Retail'" class="loans-pipeline__body">
      <processing />
    </div>
    <div v-for="type in referralTypes"
         v-show="activeTab === type"
         :key="type"
         class="loans-pipeline__body loans-pipeline__body--referrals">
      <ReferralPipeline v-if="visited[type]"
                        :type="type"
                        :me="referralMe"
                        :stages="stagesFor(type)"
                        @unread="onUnread(type, $event)" />
    </div>
  </div>
</template>

<script>
  import { Window } from '@progress/kendo-vue-dialogs';
  import { Button } from '@progress/kendo-vue-buttons';
  import auth from '../authConfig.js';
  import axios from 'axios';


  import processing from '../components/ProcessingV2.vue';
  import ReferralPipeline from '../components/referrals/ReferralPipeline.vue';
  import { lowerKeys } from '../components/referrals/referralHelpers.js';
import PageHeaderPortal from '@/views/PageHeaderPortal.vue';
  // Contacts-pipeline look while the Connect flag is on (assets/css/connectTableSkin.css); style only.
  import '@/assets/css/connectTableSkin.css';
  import { isConnectEnabled } from '@/helpers/connect/connectFeatures.js';
  import apiService from '../api/api.service.js';
  import formatHelpers from '../helpers/formatHelpers.js';

  export default {
    name: 'App',
    components: {
      PageHeaderPortal,

      'window': Window,
      'kbutton': Button,
      "processing": processing,
      ReferralPipeline
    },
    data() {
      return {
        skinOn: isConnectEnabled(),
        conditionsData: [],
        selectedLoan: '',
        selectedLoanBorrower: '',
        selectedLoanNumber: '',
        isLoading: true,
        visible: true,
        isError: false,
        loaderStatus: "Logging In",
        loanChange: false,
        loandata: [],
        // MSAL
        idTokenClaims: null,
        // Tabs: Retail = the loan pipeline; Reverse / Commercial = referral apps. Landing tab comes from the
        // API (Reverse / Commercial Admins land on theirs) unless the URL asks for one (?tab=reverse).
        activeTab: 'Retail',
        referralTypes: ['Reverse', 'Commercial'],
        visited: { Reverse: false, Commercial: false },
        referralMe: null,
        referralStages: [],
        unread: { Reverse: 0, Commercial: 0 },
        unreadTimer: null,
      }
    },
    computed: {
      tabs() {
        return [
          { key: 'Retail',     label: 'Retail Pipeline', icon: 'fa-regular fa-house',       badge: 0 },
          { key: 'Reverse',    label: 'Reverse',         icon: 'fa-regular fa-rotate-left', badge: this.unread.Reverse },
          { key: 'Commercial', label: 'Commercial',      icon: 'fa-regular fa-building',    badge: this.unread.Commercial },
        ];
      },
    },
    watch: {
      '$route.query.tab'(tab) {
        const key = this.tabFromQuery(tab);
        if (key && key !== this.activeTab) this.selectTab(key, false);
      },
    },
    mounted() {
      const fromQuery = this.tabFromQuery(this.$route.query.tab);
      if (fromQuery) this.selectTab(fromQuery, false);
      this.loadReferralSetup(!fromQuery);
      // Badges: every minute, and straight away when the user comes back to the window.
      this.unreadTimer = setInterval(this.loadUnreadCounts, 60000);
      window.addEventListener('focus', this.loadUnreadCounts);
    },
    beforeUnmount() {
      clearInterval(this.unreadTimer);
      window.removeEventListener('focus', this.loadUnreadCounts);
    },
    async created() {

      const msalAppInstance = await auth.init();

      msalAppInstance.handleRedirectPromise().then(async (tokenRes) => {

        if (!tokenRes) {
          if (!auth.user()) {
            auth.login();
          }
        }
      });
    },
    methods: {
      tabFromQuery(tab) {
        const t = String(tab || '').toLowerCase();
        return t === 'retail' ? 'Retail' : t === 'reverse' ? 'Reverse' : t === 'commercial' ? 'Commercial' : null;
      },
      selectTab(key, updateUrl = true) {
        this.activeTab = key;
        if (key !== 'Retail') this.visited[key] = true;
        if (updateUrl && this.tabFromQuery(this.$route.query.tab) !== key) {
          const query = { ...this.$route.query };
          if (key === 'Retail') delete query.tab; else query.tab = key.toLowerCase();
          this.$router.replace({ query }).catch(() => {});
        }
      },
      async loadReferralSetup(useDefaultTab) {
        try {
          const token = await auth.acquireTokenResponse();
          const [me, stages] = await Promise.all([
            apiService.GetReferralMe(token.accessToken),
            apiService.GetReferralStages(token.accessToken),
          ]);
          this.referralMe = lowerKeys(me?.data?.data);
          this.referralStages = (stages?.data?.data ?? []).map(lowerKeys);
          const landing = this.referralMe.defaulttab;
          if (useDefaultTab && this.activeTab === 'Retail' && (landing === 'Reverse' || landing === 'Commercial'))
            this.selectTab(landing, false);
        } catch (err) {
          // Tabs still work without it: everyone gets "Mine" and no status changes.
          console.warn('Referral pipeline setup failed', err);
        }
        this.loadUnreadCounts();
      },
      async loadUnreadCounts() {
        try {
          const token = await auth.acquireTokenResponse();
          const res = await apiService.GetReferralUnreadCounts(token.accessToken);
          const counts = lowerKeys(res?.data?.data);
          this.unread = { Reverse: counts.reverse || 0, Commercial: counts.commercial || 0 };
        } catch (err) {
          // Badges are a nice-to-have; keep the last counts.
        }
      },
      stagesFor(type) {
        return this.referralStages.filter(s => s.referraltype === type);
      },
      onUnread(type, count) {
        this.unread = { ...this.unread, [type]: count };
      },
    }

  }
</script>

<style scoped>
  .loans-pipeline__container {
    height: 100vh;
    overflow: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .loans-pipeline__body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
  }

  .loans-pipeline__body--referrals { flex-direction: column; padding: 12px 16px 0; }

  .pipeline-tabs { display: flex; flex-wrap: wrap; gap: 4px; padding: 8px 16px 0; border-bottom: 1px solid #e6e8ec; flex: 0 0 auto; }
  .pipeline-tab {
    position: relative; display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px; border: 0;
    border-bottom: 2px solid transparent; background: none; color: #6b7280; font-size: 14px; font-weight: 600; cursor: pointer;
  }
  .pipeline-tab:hover { color: #111827; }
  .pipeline-tab.is-on { color: #111827; border-bottom-color: var(--accent, #398b87); }
  .pipeline-tab__badge {
    min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: #dc2626; color: #fff;
    font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
    animation: pipeline-badge-pop .3s ease-out;
  }
  @keyframes pipeline-badge-pop { from { transform: scale(.6); } to { transform: scale(1); } }

.page-header-left { display: flex; align-items: center; gap: 14px; flex: 0 0 auto; }
.page-icon {
  width: 48px; height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}
.page-title  { margin: 0; font-size: 20px; font-weight: 700; color: #1f2a44; letter-spacing: -.01em; }
.page-subtitle { margin: 4px 0 0; font-size: 13px; color: #7b8397; font-weight: 500; }
</style>